import { Accordion } from "@app/shared/Accordion"
import { FC, useContext } from "react"
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { NPIStatus, ProviderBadgeStyle } from "@app/projects/enumerators";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { sortPredictedSpecialtyNames } from "@app/projects/network-assessment/provider-hub/utility";

export const ProviderProfileOverview: FC = () => {
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const { selectedProvider } = providerHubContext;

  let npiClass = "";
  let npiIcon = null;

  switch (selectedProvider.npiStatus) {
    case NPIStatus.Deactivated:
    case NPIStatus.Invalid:
      npiClass = "invalid";
      npiIcon = <QesIcon iconName={QesIcons.XCircle} isDecorative={true} />;
      break;
    case NPIStatus.Missing:
    case NPIStatus.Unknown:
      npiClass = "warning";
      npiIcon = <QesIcon iconName={QesIcons.Help} isDecorative={true} />;
      break;
    case NPIStatus.Valid:
      npiClass = "valid";
      npiIcon = <QesIcon iconName={QesIcons.CheckCircle} isDecorative={true} />;
      break;
  }

  return (
    <Accordion title="Overview" testId="provider-profile__overview-accordion" defaultOpen={true} className="provider-profile__overview">
      <dl>
        <div className="provider-profile__overview-row">
          <dt className="provider-profile__overview-label">NPI</dt>
          <dd className={`provider-profile__overview-value--npi`}>
            <span>{selectedProvider.npi || "---"}</span>
            {
              !!selectedProvider.npi
                ? <span className={npiClass}>{npiIcon} {capitalizeFirstLetter(selectedProvider.npiStatus)}</span>
                : null
            }
          </dd>
        </div>

        <div className="provider-profile__overview-row--source">
          <dt className="provider-profile__overview-label--source">Provider Source</dt>
          <dd className="provider-profile__overview-value">
            {sortProviderNetworkTypes(selectedProvider.providerNetworkTypes).map((pt) => (
              <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} providerNetworkType={pt} key={pt} />
            ))}
          </dd>
        </div>

        {!providerHubContext.isScenarioModelingProcessing && featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION) &&
          <div className="provider-profile__overview-row">
            <dt className="provider-profile__overview-label--status">Provider Action Status</dt>
            <dd className="provider-profile__overview-value--status">
              {selectedProvider.providerActionStatuses.length > 0 ? (
                selectedProvider.providerActionStatuses.map((item) => (
                  <ol key={item}>
                    <li><ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot}  status={item}/></li>
                  </ol>
                ))
              ) : (
                'No Actions Applied'
              )}
            </dd>
          </div>
        }
        {featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL) &&
          <div className="provider-profile__overview-row">
            <dt className="provider-profile__overview-label--specialty-prediction">Specialty Prediction</dt>
            <dd className="provider-profile__overview-value--specialty-prediction">
              {selectedProvider.predictedSpecialtyNames.length > 0 ? (
                <ul>
                  {sortPredictedSpecialtyNames(selectedProvider.predictedSpecialtyNames).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                '---'
              )}
            </dd>
          </div>
        }
      </dl>
    </Accordion>
  )
};